import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { ChartFormat } from './chartFormat'
import { debouncedSave } from './debouncedSave'

/** How long after the last change the format is saved. */
const SAVE_AFTER_MS = 600

/** The reader's formatting of one chart: loaded when the chart appears,
 *  applied at once on every change, and saved 600 ms after the last one, so
 *  dragging through a select does not send a request per step. A change
 *  still waiting when the chart goes (a new time window remounts it) is
 *  saved then, not dropped; a change made before the saved format has
 *  loaded is kept over it. Reset drops it on the server too. Without a key
 *  (no chart named) it does nothing. */
export function useChartFormat(key: string | undefined) {
  const [format, setFormat] = useState<ChartFormat | null>(null)
  const [error, setError] = useState<string>()
  const saver = useRef<ReturnType<typeof debouncedSave<ChartFormat>> | null>(null)
  const touched = useRef(false)

  useEffect(() => {
    let stale = false
    touched.current = false
    setFormat(null)
    setError(undefined)
    if (!key) return
    const save = debouncedSave<ChartFormat>((next) => {
      api.chartFormat.put(key, next).then(
        () => { if (!stale) setError(undefined) },
        (failed: unknown) => {
          if (!stale) {
            setError(failed instanceof Error ? failed.message : 'Could not save the formatting.')
          }
        })
    }, SAVE_AFTER_MS)
    saver.current = save
    api.chartFormat.get(key).then(
      ({ settings }) => { if (!stale && !touched.current) setFormat(settings) },
      () => { /* no saved format: the chart draws as it always does */ })
    return () => {
      stale = true
      save.flush()
      saver.current = null
    }
  }, [key])

  const change = useCallback((next: ChartFormat) => {
    touched.current = true
    setFormat(next)
    saver.current?.schedule(next)
  }, [])

  const reset = useCallback(() => {
    touched.current = true
    saver.current?.cancel()
    setFormat(null)
    if (!key) return
    api.chartFormat.reset(key).catch((failed: unknown) =>
      setError(failed instanceof Error ? failed.message : 'Could not reset the formatting.'))
  }, [key])

  return { format, error, change, reset }
}
